import React from "react";
import { motion } from "framer-motion";
import { getLucideIcon } from "../lib/icons";
import { FileText, Sparkles, Maximize2 } from "lucide-react";

// order maps 8 outer elements around the center (index 4)
const SLOTS = [0, 1, 2, 3, "center", 4, 5, 6, 7];

export default function FractalGrid({ grid, atMaxDepth, onCellClick, onExpand }) {
  const { topic, elements, from_notes, path } = grid;

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-3 sm:grid-rows-3 gap-3 sm:gap-4 w-full max-w-5xl mx-auto sm:aspect-square min-h-[560px]"
      data-testid="fractal-grid"
    >
      {SLOTS.map((slot, i) => {
        if (slot === "center") {
          return (
            <motion.div
              key="center"
              layout
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 180, damping: 18 }}
              data-testid="grid-cell-center"
              className="center-pulse relative overflow-hidden rounded-2xl p-5 flex flex-col justify-between bg-gradient-to-br from-purple-800/40 via-purple-950/60 to-black/80 border-2 border-purple-500/80 z-10 sm:scale-105 grid-noise"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono-fx text-[10px] uppercase tracking-widest text-purple-300">
                  Layer {path.length} / 6
                </span>
                {from_notes ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 flex items-center gap-1">
                    <FileText className="w-3 h-3" /> from your notes
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-400/30 text-purple-200 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> core concept
                  </span>
                )}
              </div>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-white glow-text leading-tight break-normal hyphens-auto">
                  {topic}
                </h2>
                <p className="mt-2 text-xs text-purple-200/70">8 key sub-topics mapped around this concept.</p>
              </div>
            </motion.div>
          );
        }

        const el = elements[slot];
        if (!el) return <div key={`empty-${slot}`} />;
        const Icon = getLucideIcon(el.icon_keyword);
        return (
          <motion.button
            key={`${el.label}-${slot}`}
            layout
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            data-testid={`grid-cell-outer-${slot}`}
            onClick={() => onCellClick(el)}
            disabled={atMaxDepth}
            className="group relative overflow-hidden rounded-2xl p-4 sm:p-5 text-left flex flex-col justify-between bg-slate-900/60 backdrop-blur-md border border-purple-500/20 hover:border-purple-400/60 hover:bg-purple-950/40 hover:-translate-y-1 hover:shadow-[0_0_22px_rgba(168,85,247,0.28)] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="flex items-start justify-between">
              <div className="p-2 rounded-xl bg-purple-500/12 border border-purple-500/25 group-hover:bg-purple-500/25 transition-colors">
                <Icon className="w-5 h-5 text-purple-300 group-hover:text-purple-100" />
              </div>
              <div className="flex items-center gap-1.5">
                {from_notes && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/25 text-emerald-300 flex items-center gap-1">
                    <FileText className="w-2.5 h-2.5" /> notes
                  </span>
                )}
                <span
                  role="button"
                  tabIndex={0}
                  data-testid={`grid-cell-expand-${slot}`}
                  onClick={(e) => { e.stopPropagation(); onExpand && onExpand(el); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onExpand && onExpand(el); } }}
                  title="Expand details"
                  className="p-1 rounded-md text-purple-300/60 hover:text-white hover:bg-purple-500/20 transition-colors"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
            <div className="mt-3">
              <h3 className="font-display text-base font-bold text-white leading-tight">{el.label}</h3>
              <p className="mt-1 text-xs text-purple-200/65 leading-snug line-clamp-3">{el.description}</p>
            </div>
            {!atMaxDepth && (
              <span className="mt-3 text-[10px] font-mono-fx uppercase tracking-wider text-purple-400/70 group-hover:text-purple-300">
                Drill down →
              </span>
            )}
          </motion.button>
        );
      })}
    </div>
  );
}
